import React from "react";
import { Navigate, Routes, Route, Link, useLocation } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useContent } from "../../lib/content";
import SEO from "../../components/SEO";
import AdminProducts from "./AdminProducts";
import AdminArticles from "./AdminArticles";
import AdminSettings from "./AdminSettings";
import AdminLanding from "./AdminLanding";
import AdminAbout from "./AdminAbout";
import AdminContact from "./AdminContact";
import AdminCategories from "./AdminCategories";
import { LogOut, Package, BookOpen, Home, Settings, Cloud, CloudOff, Grid3x3, Phone, LayoutDashboard } from "lucide-react";

export default function AdminDashboard() {
  const { status, loading, logout, r2 } = useAuth();
  const { loaded } = useContent();
  const location = useLocation();

  if (loading || !loaded) {
    return (
      <div className="py-32 text-center text-inkMuted font-display">
        در حال بارگذاری...
      </div>
    );
  }

  if (status !== "authenticated") return <Navigate to="/admin" replace />;

  const tabs = [
    { to: "/admin/dashboard", label: "محصولات", icon: Package, exact: true },
    { to: "/admin/dashboard/articles", label: "مقالات", icon: BookOpen },
    { to: "/admin/dashboard/categories", label: "دسته‌بندی‌ها", icon: Grid3x3 },
    { to: "/admin/dashboard/landing", label: "صفحه اصلی", icon: LayoutDashboard },
    { to: "/admin/dashboard/about", label: "درباره ما", icon: BookOpen },
    { to: "/admin/dashboard/contact", label: "تماس", icon: Phone },
    { to: "/admin/dashboard/settings", label: "تنظیمات", icon: Settings },
  ];

  return (
    <>
      <SEO title="پنل مدیریت · تحسین" />
      <div className="admin-scope max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-10" data-testid="admin-dashboard">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="editorial-tag mb-2">ADMIN · پنل</div>
            <h1 className="font-display text-3xl md:text-4xl font-black text-ink">داشبورد</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 bg-white border border-honey-200 rounded-full px-4 py-2 text-sm">
              {r2 ? (
                <>
                  <Cloud className="w-4 h-4 text-olive-600" />
                  <span>متصل به R2</span>
                </>
              ) : (
                <>
                  <CloudOff className="w-4 h-4 text-ember" />
                  <span>ذخیره‌سازی محلی</span>
                </>
              )}
            </div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-white border border-honey-200 hover:border-honey-500 text-ink font-display text-sm px-5 py-2 rounded-full transition-colors"
              data-testid="admin-home-link"
            >
              <Home className="w-4 h-4" /> سایت
            </Link>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-700 font-display text-sm px-5 py-2 rounded-full transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" /> خروج
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-8 border-b border-honey-200 overflow-x-auto whitespace-nowrap">
          {tabs.map((t) => {
            const active = t.exact
              ? location.pathname === t.to
              : location.pathname.startsWith(t.to);
            return (
              <Link
                key={t.to}
                to={t.to}
                className={`inline-flex items-center gap-2 px-5 py-3 -mb-px border-b-2 transition-colors font-display text-sm ${
                  active
                    ? "border-honey-500 text-honey-600"
                    : "border-transparent text-inkMuted hover:text-honey-600"
                }`}
                data-testid={`admin-tab-${t.to.replace("/admin/dashboard", "").replace("/", "") || "products"}`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </Link>
            );
          })}
        </div>

        <Routes>
          <Route index element={<AdminProducts />} />
          <Route path="articles" element={<AdminArticles />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="landing" element={<AdminLanding />} />
          <Route path="about" element={<AdminAbout />} />
          <Route path="contact" element={<AdminContact />} />
          <Route path="settings" element={<AdminSettings />} />
        </Routes>
      </div>
    </>
  );
}
